import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, ChevronDown } from 'lucide-react';
import { useLang, pickLocalized, tx } from '../i18n/LanguageContext';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { getMergedCourses } from '../lib/courses';
import { cacheCourses } from '../lib/courseCache';
import { hasAccess } from '../components/LockedDialog';
import { useCategories } from '../hooks/useCategories';
import { CoursesHero } from '../components/courses/CoursesHero';
import { FilterBar } from '../components/courses/FilterChips';
import { CourseCard } from '../components/courses/CourseCard';
import { levelKey, catsOf } from '../components/courses/meta';

const PAGE = 9;

const Courses = () => {
  const { lang } = useLang();
  const L = (ar, en) => tx(lang, ar, en);
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [q, setQ] = useState(searchParams.get('q') || '');
  const [cat, setCat] = useState(searchParams.get('cat') || 'all');
  const [level, setLevel] = useState('all');
  const [freeOnly, setFreeOnly] = useState(false);
  const [sort, setSort] = useState('latest');
  const [visible, setVisible] = useState(PAGE);
  const [courses, setCourses] = useState(getMergedCourses([]));
  const [loading, setLoading] = useState(true);
  const { categories, getLabel } = useCategories();
  const canAccess = hasAccess(user);

  useEffect(() => { setQ(searchParams.get('q') || ''); setCat(searchParams.get('cat') || 'all'); }, [searchParams]);
  useEffect(() => {
    api.get('/courses').then(({ data }) => { cacheCourses(data); setCourses(getMergedCourses(data)); }).catch(() => {}).finally(() => setLoading(false));
  }, []);
  useEffect(() => { setVisible(PAGE); }, [q, cat, level, freeOnly, sort]);

  const catCounts = useMemo(() => courses.reduce((m, c) => { catsOf(c).forEach(v => { m[v] = (m[v] || 0) + 1; }); return m; }, {}), [courses]);
  const cats = categories.filter(c => catCounts[c.value]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const list = courses.filter(c => {
      const cs = catsOf(c);
      const text = [pickLocalized(c.title, lang), pickLocalized(c.brand, lang), pickLocalized(c.subtitle, lang), ...cs, ...cs.map(v => getLabel(v, lang))].join(' ').toLowerCase();
      return (!needle || text.includes(needle))
        && (cat === 'all' || cs.includes(cat))
        && (level === 'all' || levelKey(pickLocalized(c.level, lang)) === level)
        && (!freeOnly || c.is_free);
    });
    return [...list].sort((a, b) => {
      if (sort === 'popular') return (b.students || 0) - (a.students || 0);
      if (sort === 'hours_asc') return (a.seconds || 0) - (b.seconds || 0);
      if (sort === 'hours_desc') return (b.seconds || 0) - (a.seconds || 0);
      return Number(!!b.featured) - Number(!!a.featured) || String(b.created_at || '').localeCompare(String(a.created_at || ''));
    });
  }, [courses, q, cat, level, freeOnly, sort, lang]); // eslint-disable-line react-hooks/exhaustive-deps

  const shown = Math.min(visible, filtered.length);

  return (
    <main data-testid="courses-page" className="page-y min-h-screen bg-gradient-to-b from-blue-50/70 via-slate-50/40 to-white">
      <div className="site-container">
        <CoursesHero q={q} setQ={setQ} />
        <FilterBar cats={cats} cat={cat} setCat={setCat} level={level} setLevel={setLevel} freeOnly={freeOnly} setFreeOnly={setFreeOnly} sort={sort} setSort={setSort} getLabel={getLabel} />
        <div data-testid="courses-count" className="text-sm text-slate-500 mt-5 mb-4">{L(`عرض ${shown} كورسات من إجمالي ${filtered.length} كورسات`, `Showing ${shown} of ${filtered.length} courses`)}</div>

        {loading ? <div className="flex justify-center py-16"><Loader2 className="w-7 h-7 animate-spin text-blue-600" /></div>
          : filtered.length === 0 ? <div data-testid="courses-empty" className="text-center text-slate-500 py-16 text-sm rounded-2xl bg-white border border-dashed border-slate-200">{L('لا توجد كورسات تطابق البحث', 'No courses match your search')}</div>
          : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
                {filtered.slice(0, visible).map(c => <CourseCard key={c.id} course={c} locked={!canAccess && !c.is_free} onOpen={(id) => navigate(`/courses/${id}`)} />)}
              </div>
              {visible < filtered.length && (
                <div className="flex justify-center mt-8">
                  <button type="button" data-testid="load-more-btn" onClick={() => setVisible(v => v + PAGE)} className="h-11 px-6 rounded-xl bg-white border border-blue-200 text-blue-600 text-sm font-bold inline-flex items-center gap-2 hover:bg-blue-50 transition-colors">
                    <ChevronDown className="w-4 h-4" />{L('عرض المزيد من الكورسات', 'Show more courses')}
                  </button>
                </div>
              )}
            </>
          )}
      </div>
    </main>
  );
};

export default Courses;
